import {
  Uint8ArrayReader,
  Uint8ArrayWriter,
  ZipReader,
  type Entry,
  type FileEntry,
} from "@zip.js/zip.js";
import {
  applyRecoveryRecipe,
  type RecoveryRecipe,
} from "./deobfuscation";
import {
  classifyEntryPath,
  detectFileFormat,
  type DetectedFileFormat,
} from "./file-formats";

const MAX_CONTAINER_ENTRIES = 12_000;
const MAX_CONTAINER_EXPANDED_BYTES = 256 * 1024 * 1024;
const MAX_ENTRY_OUTPUT_BYTES = 64 * 1024 * 1024;
const MAX_PASSWORD_ATTEMPTS = 512;

export interface ContainerEntryInfo {
  path: string;
  directory: boolean;
  size: number;
  compressedSize: number;
  encrypted: boolean;
  zipCrypto: boolean;
  executable: boolean;
  kind: ReturnType<typeof classifyEntryPath>;
}

export interface ContainerInspection {
  format: DetectedFileFormat;
  supported: boolean;
  entries: ContainerEntryInfo[];
  totalExpandedBytes: number;
  encryptedEntries: number;
  prependedBytes: number;
  warnings: string[];
}

export interface PasswordRecoveryProgress {
  attempt: number;
  total: number;
}

export interface PasswordRecoveryResult {
  found: boolean;
  password?: string;
  attempts: number;
  encryptedEntries: number;
  stopped: "found" | "exhausted" | "cancelled" | "not-encrypted";
}

const BUILT_IN_PASSWORDS = [
  "infected",
  "malware",
  "virus",
  "sample",
  "password",
  "Password",
  "PASSWORD",
  "password123",
  "p@ssw0rd",
  "1234",
  "12345",
  "123456",
  "12345678",
  "123456789",
  "0000",
  "1111",
  "qwerty",
  "letmein",
  "admin",
  "root",
  "secret",
  "test",
  "archive",
  "payload",
  "minecraft",
  "Minecraft",
  "mod",
  "client",
  "cheat",
  "free",
  "download",
  "jar",
  "jarlab",
  "krypton",
  "weedhack",
  "silentnet",
  "fractureiser",
];

function owned(bytes: Uint8Array) {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy;
}

function normalizedName(path: string) {
  return path.replaceAll("\\", "/").replace(/^\/+/, "");
}

function findZipStart(bytes: Uint8Array) {
  const limit = Math.min(bytes.length - 4, 1024 * 1024);
  for (let index = 0; index <= limit; index += 1) {
    if (
      bytes[index] === 0x50 &&
      bytes[index + 1] === 0x4b &&
      ((bytes[index + 2] === 0x03 && bytes[index + 3] === 0x04) ||
        (bytes[index + 2] === 0x05 && bytes[index + 3] === 0x06) ||
        (bytes[index + 2] === 0x07 && bytes[index + 3] === 0x08))
    ) {
      return index;
    }
  }
  return -1;
}

function zipBytes(bytes: Uint8Array, format: DetectedFileFormat) {
  if (format.id === "java-jmod" && bytes.length > 4) {
    return { bytes: owned(bytes.subarray(4)), prependedBytes: 4 };
  }
  const offset = findZipStart(bytes);
  if (offset > 0) {
    return { bytes: owned(bytes.subarray(offset)), prependedBytes: offset };
  }
  return { bytes: owned(bytes), prependedBytes: 0 };
}

async function openZipEntries(bytes: Uint8Array, format: DetectedFileFormat) {
  const normalized = zipBytes(bytes, format);
  const reader = new ZipReader(new Uint8ArrayReader(normalized.bytes), {
    useWebWorkers: false,
    strictness: "balanced",
  });
  try {
    const entries = await reader.getEntries({
      onprogress: (index) => {
        if (index > MAX_CONTAINER_ENTRIES) {
          throw new Error(
            `Container exceeds the ${MAX_CONTAINER_ENTRIES.toLocaleString()}-entry inspection limit.`,
          );
        }
      },
    });
    if (entries.length > MAX_CONTAINER_ENTRIES) {
      throw new Error(
        `Container contains ${entries.length.toLocaleString()} entries; the safe limit is ${MAX_CONTAINER_ENTRIES.toLocaleString()}.`,
      );
    }
    const expanded = entries.reduce(
      (total, entry) => total + (entry.directory ? 0 : entry.uncompressedSize),
      0,
    );
    return { reader, entries, expanded, ...normalized };
  } catch (error) {
    await reader.close().catch(() => undefined);
    throw error;
  }
}

function tarString(bytes: Uint8Array, offset: number, length: number) {
  return new TextDecoder("utf-8", { fatal: false })
    .decode(bytes.subarray(offset, offset + length))
    .replace(/\0.*$/, "")
    .trim();
}

function tarNumber(bytes: Uint8Array, offset: number, length: number) {
  const value = tarString(bytes, offset, length).replace(/\s/g, "");
  return value ? Number.parseInt(value, 8) || 0 : 0;
}

interface TarEntry extends ContainerEntryInfo {
  dataOffset: number;
}

function parseTar(bytes: Uint8Array) {
  const entries: TarEntry[] = [];
  let offset = 0;
  let totalExpandedBytes = 0;
  while (offset + 512 <= bytes.length && entries.length < MAX_CONTAINER_ENTRIES) {
    const header = bytes.subarray(offset, offset + 512);
    if (header.every((byte) => byte === 0)) break;
    const name = tarString(header, 0, 100);
    const prefix = tarString(header, 345, 155);
    const path = normalizedName(prefix ? `${prefix}/${name}` : name);
    const size = tarNumber(header, 124, 12);
    const type = String.fromCharCode(header[156] || 0);
    const directory = type === "5" || path.endsWith("/");
    const dataOffset = offset + 512;
    if (!path || dataOffset + size > bytes.length) {
      throw new Error("The TAR header points outside the available file.");
    }
    totalExpandedBytes += directory ? 0 : size;
    if (totalExpandedBytes > MAX_CONTAINER_EXPANDED_BYTES) {
      throw new Error("TAR content exceeds the 256 MB inspection limit.");
    }
    entries.push({
      path,
      directory,
      size,
      compressedSize: size,
      encrypted: false,
      zipCrypto: false,
      executable: false,
      kind: classifyEntryPath(path, directory),
      dataOffset,
    });
    offset = dataOffset + Math.ceil(size / 512) * 512;
  }
  if (entries.length >= MAX_CONTAINER_ENTRIES) {
    throw new Error("TAR entry-count limit reached.");
  }
  return { entries, totalExpandedBytes };
}

function publicTarEntry(entry: TarEntry): ContainerEntryInfo {
  return {
    path: entry.path,
    directory: entry.directory,
    size: entry.size,
    compressedSize: entry.compressedSize,
    encrypted: entry.encrypted,
    zipCrypto: entry.zipCrypto,
    executable: entry.executable,
    kind: entry.kind,
  };
}

async function gunzip(bytes: Uint8Array) {
  const recipe: RecoveryRecipe = {
    id: "gzip",
    label: "Decompress GZIP",
    steps: [{ kind: "gzip" }],
  };
  return applyRecoveryRecipe(bytes, recipe, MAX_CONTAINER_EXPANDED_BYTES);
}

export async function inspectContainer(
  bytes: Uint8Array,
  path: string,
): Promise<ContainerInspection> {
  const format = detectFileFormat(bytes, path);
  if (
    format.id === "zip" ||
    format.id === "java-archive" ||
    format.id === "java-war" ||
    format.id === "java-ear" ||
    format.id === "android-apk" ||
    format.id === "android-bundle" ||
    format.id === "apple-ipa" ||
    format.id === "java-jmod" ||
    format.id === "minecraft-pack" ||
    format.id === "modrinth-pack" ||
    format.id === "openxml-word" ||
    format.id === "openxml-excel" ||
    format.id === "openxml-powerpoint" ||
    format.id === "visual-studio-extension" ||
    format.id === "nuget-package" ||
    format.id === "python-wheel" ||
    format.id === "browser-extension" ||
    format.id === "chrome-extension"
  ) {
    const opened = await openZipEntries(bytes, format);
    try {
      const warnings: string[] = [];
      if (opened.expanded > MAX_CONTAINER_EXPANDED_BYTES) {
        warnings.push(
          "Expanded content exceeds 256 MB. Entries remain listed, but oversized extraction is blocked.",
        );
      }
      if (opened.prependedBytes) {
        warnings.push(
          `${opened.prependedBytes.toLocaleString()} wrapper bytes precede the ZIP data.`,
        );
      }
      return {
        format,
        supported: true,
        entries: opened.entries.map((entry) => ({
          path: normalizedName(entry.filename),
          directory: entry.directory,
          size: entry.directory ? 0 : entry.uncompressedSize,
          compressedSize: entry.directory ? 0 : entry.compressedSize,
          encrypted: entry.encrypted,
          zipCrypto: entry.zipCrypto,
          executable: entry.executable,
          kind: classifyEntryPath(entry.filename, entry.directory),
        })),
        totalExpandedBytes: opened.expanded,
        encryptedEntries: opened.entries.filter(
          (entry) => !entry.directory && entry.encrypted,
        ).length,
        prependedBytes: opened.prependedBytes,
        warnings,
      };
    } finally {
      await opened.reader.close();
    }
  }
  if (format.id === "tar") {
    const tar = parseTar(bytes);
    return {
      format,
      supported: true,
      entries: tar.entries.map(publicTarEntry),
      totalExpandedBytes: tar.totalExpandedBytes,
      encryptedEntries: 0,
      prependedBytes: 0,
      warnings: [],
    };
  }
  if (format.id === "gzip") {
    const output = await gunzip(bytes);
    const innerFormat = detectFileFormat(output, path.replace(/\.gz(?:ip)?$/i, ""));
    if (innerFormat.id === "tar") {
      const tar = parseTar(output);
      return {
        format,
        supported: true,
        entries: tar.entries.map(publicTarEntry),
        totalExpandedBytes: tar.totalExpandedBytes,
        encryptedEntries: 0,
        prependedBytes: 0,
        warnings: [`GZIP stream contains a ${innerFormat.label}.`],
      };
    }
    const name =
      path
        .split(/[\\/]/)
        .pop()
        ?.replace(/\.gz(?:ip)?$/i, "") || "decompressed.bin";
    return {
      format,
      supported: true,
      entries: [
        {
          path: name,
          directory: false,
          size: output.length,
          compressedSize: bytes.length,
          encrypted: false,
          zipCrypto: false,
          executable: innerFormat.executable,
          kind: innerFormat.category,
        },
      ],
      totalExpandedBytes: output.length,
      encryptedEntries: 0,
      prependedBytes: 0,
      warnings: [`GZIP stream expands to ${innerFormat.label}.`],
    };
  }
  return {
    format,
    supported: false,
    entries: [],
    totalExpandedBytes: 0,
    encryptedEntries: 0,
    prependedBytes: 0,
    warnings: [
      `${format.label} is recognized, but safe in-browser extraction is not available for this codec.`,
      "Download the entry for analysis in an isolated specialist tool. JarLab will not execute external unpackers.",
    ],
  };
}

async function withZipFileEntry<T>(
  bytes: Uint8Array,
  path: string,
  entryPath: string,
  callback: (entry: FileEntry) => Promise<T>,
) {
  const format = detectFileFormat(bytes, path);
  const opened = await openZipEntries(bytes, format);
  try {
    const target = opened.entries.find(
      (entry): entry is FileEntry =>
        !entry.directory && normalizedName(entry.filename) === normalizedName(entryPath),
    );
    if (!target) throw new Error("The selected container entry no longer exists.");
    return await callback(target);
  } finally {
    await opened.reader.close();
  }
}

export async function extractContainerEntry(
  bytes: Uint8Array,
  path: string,
  entryPath: string,
  password?: string,
) {
  const format = detectFileFormat(bytes, path);
  if (format.id === "tar") {
    const target = parseTar(bytes).entries.find(
      (entry) => normalizedName(entry.path) === normalizedName(entryPath),
    );
    if (!target || target.directory) throw new Error("TAR entry was not found.");
    if (target.size > MAX_ENTRY_OUTPUT_BYTES) {
      throw new Error("The selected entry exceeds the 64 MB extraction limit.");
    }
    return owned(bytes.subarray(target.dataOffset, target.dataOffset + target.size));
  }
  if (format.id === "gzip") {
    const output = await gunzip(bytes);
    const innerFormat = detectFileFormat(output, path.replace(/\.gz(?:ip)?$/i, ""));
    if (innerFormat.id === "tar") {
      const target = parseTar(output).entries.find(
        (entry) => normalizedName(entry.path) === normalizedName(entryPath),
      );
      if (!target || target.directory) throw new Error("TAR entry was not found.");
      if (target.size > MAX_ENTRY_OUTPUT_BYTES) {
        throw new Error("The selected entry exceeds the 64 MB extraction limit.");
      }
      return owned(
        output.subarray(target.dataOffset, target.dataOffset + target.size),
      );
    }
    if (output.length > MAX_ENTRY_OUTPUT_BYTES) {
      throw new Error("The decompressed stream exceeds the 64 MB extraction limit.");
    }
    return output;
  }
  return withZipFileEntry(bytes, path, entryPath, async (entry) => {
    if (entry.uncompressedSize > MAX_ENTRY_OUTPUT_BYTES) {
      throw new Error("The selected entry exceeds the 64 MB extraction limit.");
    }
    const result = await entry.getData(
      new Uint8ArrayWriter(Math.min(entry.uncompressedSize || 65_536, 512 * 1024)),
      {
        password,
        checkSignature: true,
        checkOverlappingEntry: true,
        useWebWorkers: false,
      },
    );
    return owned(result);
  });
}

function passwordCandidates(
  manual: string[],
  contextual: string[],
  path: string,
) {
  const stem =
    path
      .split(/[\\/]/)
      .pop()
      ?.replace(/\.[^.]+$/, "") ?? "";
  const values = [
    ...manual,
    ...contextual,
    stem,
    stem.toLowerCase(),
    ...BUILT_IN_PASSWORDS,
  ];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const clean = value.trim();
    if (!clean || clean.length > 128 || seen.has(clean)) continue;
    seen.add(clean);
    result.push(clean);
    if (result.length >= MAX_PASSWORD_ATTEMPTS) break;
  }
  return result;
}

export async function recoverZipPassword(
  bytes: Uint8Array,
  path: string,
  manualCandidates: string[] = [],
  contextualCandidates: string[] = [],
  onProgress?: (progress: PasswordRecoveryProgress) => void,
  signal?: AbortSignal,
): Promise<PasswordRecoveryResult> {
  const format = detectFileFormat(bytes, path);
  const opened = await openZipEntries(bytes, format);
  try {
    const encrypted = opened.entries
      .filter((entry): entry is FileEntry => !entry.directory && entry.encrypted)
      .sort(
        (left, right) => left.compressedSize - right.compressedSize,
      );
    if (!encrypted.length) {
      return {
        found: false,
        attempts: 0,
        encryptedEntries: 0,
        stopped: "not-encrypted",
      };
    }
    const target = encrypted[0];
    const candidates = passwordCandidates(manualCandidates, contextualCandidates, path);
    for (let index = 0; index < candidates.length; index += 1) {
      if (signal?.aborted) {
        return {
          found: false,
          attempts: index,
          encryptedEntries: encrypted.length,
          stopped: "cancelled",
        };
      }
      const password = candidates[index];
      onProgress?.({ attempt: index + 1, total: candidates.length });
      try {
        await target.getData(new Uint8ArrayWriter(1), {
          password,
          checkPasswordOnly: true,
          checkSignature: true,
          useWebWorkers: false,
        });
        return {
          found: true,
          password,
          attempts: index + 1,
          encryptedEntries: encrypted.length,
          stopped: "found",
        };
      } catch {
        // Wrong passwords are expected. Continue within the fixed attempt budget.
      }
      if (index % 24 === 23) {
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }
    }
    return {
      found: false,
      attempts: candidates.length,
      encryptedEntries: encrypted.length,
      stopped: "exhausted",
    };
  } finally {
    await opened.reader.close();
  }
}

export function containerLimits() {
  return {
    maxEntries: MAX_CONTAINER_ENTRIES,
    maxExpandedBytes: MAX_CONTAINER_EXPANDED_BYTES,
    maxEntryBytes: MAX_ENTRY_OUTPUT_BYTES,
    maxPasswordAttempts: MAX_PASSWORD_ATTEMPTS,
  };
}

export function isZipEntry(entry: Entry): entry is FileEntry {
  return !entry.directory;
}
